import { useContext, useState, useEffect, useRef, useCallback } from 'react';
import EmailContext, { EmailContextType } from '../contexts/EmailContext';
import { Email } from '../types/email';
import { welcomeEmail } from '../utils/defaultEmails';

// Polling interval in milliseconds
const POLLING_INTERVAL = 30000; // 30 seconds

export function useEmail(): EmailContextType & { 
  pollingEnabled: boolean;
  setPollingEnabled: (enabled: boolean) => void;
} {
  const context = useContext(EmailContext);
  const [isInitiallyLoading, setIsInitiallyLoading] = useState(true);
  const [pollingEnabled, setPollingEnabled] = useState(true);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);
  const lastFetchRef = useRef<number | null>(null);

  if (context === null) {
    throw new Error('useEmail must be used within an EmailProvider');
  }

  // Smart merge function that combines existing emails with new ones
  const smartMergeEmails = useCallback((existingEmails: Email[], newEmails: Email[]): Email[] => {
    // Create a map of existing emails by ID
    const existingMap = new Map<number | string, Email>();
    
    // Keep track of welcome email separately
    let welcomeEmailEntry: Email | null = null;
    
    // First, process existing emails
    existingEmails.forEach(email => {
      if (email.id === -1) {
        // This is the welcome email, handle separately
        welcomeEmailEntry = email;
      } else {
        existingMap.set(email.id, email);
      }
    });
    
    // Process new emails - add or update
    newEmails.forEach(email => {
      // Skip if this is a welcome email in the API response
      if (email.subject === 'Welcome to MailDerp' && email.from === 'team@mailderp.com') {
        return;
      }

      // Check if we have this email already
      const existing = existingMap.get(email.id);
      if (existing) {
        // Preserve read state from existing email
        existingMap.set(email.id, {
          ...email,
          read: existing.read
        });
      } else {
        // It's a new email
        existingMap.set(email.id, email);
      }
    });
    
    // Create a set of IDs from new emails for comparison
    const newEmailIds = new Set(newEmails.map(email => email.id));
    
    // If an email isn't in the new response, remove it
    existingEmails.forEach(email => {
      if (email.id !== -1 && !newEmailIds.has(email.id)) {
        existingMap.delete(email.id);
      }
    });
    
    // Convert map back to array and sort by date (newest first)
    const mergedEmails = Array.from(existingMap.values()).sort((a, b) => 
      new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    
    // Always add welcome email at the beginning if we have one
    if (welcomeEmailEntry) {
      return [welcomeEmailEntry, ...mergedEmails];
    }
    
    return mergedEmails;
  }, []);

  // Enhanced fetchEmails that uses smart merging
  const fetchAndMergeEmails = useCallback(async () => {
    if (!context.currentInbox) return;
    
    // Add a timestamp check
    const now = Date.now();
    if (lastFetchRef.current && now - lastFetchRef.current < POLLING_INTERVAL) {
      console.log('Skipping fetch - too soon since last request');
      return;
    }
    lastFetchRef.current = now;
    
    try {
      const emailAddress = context.currentInbox.email;
      const response = await fetch(`/api/email/${encodeURIComponent(emailAddress)}`);
      
      if (!response.ok) {
        throw new Error(`Error fetching emails: ${response.status}`);
      }
      
      const newEmails = await response.json();
      
      // Create a custom welcome email for this inbox
      const customWelcomeEmail = {
        ...welcomeEmail,
        to: emailAddress,
        date: new Date()
      };
      
      // Merge the emails with existing ones
      context.setEmails(prevEmails => 
        smartMergeEmails(prevEmails, [customWelcomeEmail, ...newEmails])
      );
      
    } catch (error) {
      console.error('Failed to fetch emails:', error);
      // Don't reset emails on error, keep the existing ones
    }
  }, [context.currentInbox, smartMergeEmails]);

  // Set up polling
  useEffect(() => {
    if (pollingEnabled && context.currentInbox) {
      // Initial fetch
      // fetchAndMergeEmails();
      
      // Set up interval (also kicks off initial fetch)
      pollingRef.current = setInterval(fetchAndMergeEmails, POLLING_INTERVAL);
    }
    
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [pollingEnabled, context.currentInbox, fetchAndMergeEmails]);

  // Handle initial loading state
  useEffect(() => {
    if (isInitiallyLoading && !context.loading) {
      const timer = setTimeout(() => {
        setIsInitiallyLoading(false);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isInitiallyLoading, context.loading]);

  return {
    ...context,
    loading: context.loading || isInitiallyLoading,
    pollingEnabled,
    setPollingEnabled,
  };
}